import { capitalize, compact } from 'lodash';

import { EMISSIONS_DATA_SOURCES, MODE_AIR, MODE_RAIL, MODE_ROAD, MODE_WATER, VEHICLE_TYPES } from '@/lib/constants';
import { EmissionsDetailBreakdownDataSource } from '@/lib/models/emissions/types';
import { transformItineraryToCalculatorFormData } from '@/lib/models/emissions/utils/transform-to-calculator-form-data/transform-itinerary-to-calculator-form-data';
import { transformRouteOptionToCalculatorFormData } from '@/lib/models/emissions/utils/transform-to-calculator-form-data/transform-route-option-to-calculator-form-data';
import { transformRouteToCalculatorFormData } from '@/lib/models/emissions/utils/transform-to-calculator-form-data/transform-route-to-calculator-form-data';
import { getItineraryLocations } from '@/lib/models/itinerary/utils/get-itinerary-locations';
import { LineDictionaryModel } from '@/lib/models/lines/types';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { transformRouteScheduleToItinerary } from '@/lib/transformers/itinerary';
import { CargoOption, EmissionsFormInputsInterface, EmissionsModeBreakdown, RadialChartData } from '@/types';
import { getTransportColor, getTransportTextColor } from '@/utils/helpers/get-transport-color';

const EMISSION_MODES: { mode: keyof EmissionsModeBreakdown; alternativeLabel?: string }[] = [
  { mode: MODE_ROAD },
  { mode: MODE_AIR },
  { mode: MODE_WATER, alternativeLabel: capitalize(VEHICLE_TYPES.VESSEL) },
  { mode: MODE_RAIL },
];

export const transformModeBreakdownToRadialData = (data: EmissionsModeBreakdown): RadialChartData[] => {
  const output = EMISSION_MODES.map(({ mode, alternativeLabel }): RadialChartData | null => {
    const emissionValue = data?.[mode] as number;
    if (!emissionValue) return null;

    const color = getTransportColor(mode);
    const textColor = getTransportTextColor(mode);

    return {
      name: `${capitalize(alternativeLabel || mode)} emissions`,
      value: emissionValue,
      percent: 0,
      color,
      textColor,
    };
  });

  const rows = compact(output);
  const totalValue = rows.reduce((a, b) => a + b.value, 0);

  return rows.map((row) => {
    return {
      ...row,
      percent: Math.round((row.value / totalValue) * 100),
    };
  });
};

export const getFormDataFromSource = ({
  source,
  locations,
  lineDictionary,
  cargoParams,
}: {
  source?: EmissionsDetailBreakdownDataSource;
  locations: LocationDictionaryModel;
  lineDictionary: LineDictionaryModel;
  cargoParams?: CargoOption;
}): EmissionsFormInputsInterface => {
  if (!source) {
    throw new Error('No valid data source provided for emissions calculator');
  }

  switch (source.type) {
    case EMISSIONS_DATA_SOURCES.ROUTE:
      return transformRouteToCalculatorFormData({
        route: source.data,
        locations,
        cargoParams,
      });

    case EMISSIONS_DATA_SOURCES.OPTION:
      return transformRouteOptionToCalculatorFormData({
        option: source.data,
        locations,
        cargoParams,
      });

    case EMISSIONS_DATA_SOURCES.ITINERARY: {
      const itineraryLocations = getItineraryLocations(source.data.legs, locations);
      return transformItineraryToCalculatorFormData({
        locations: itineraryLocations,
        itinerary: source.data,
        cargoParams,
      });
    }

    case EMISSIONS_DATA_SOURCES.ROUTE_SCHEDULE: {
      const transformedItinerary = transformRouteScheduleToItinerary(source.data, lineDictionary);
      const routeScheduleLocations = getItineraryLocations(transformedItinerary.legs, locations);
      return transformItineraryToCalculatorFormData({
        locations: routeScheduleLocations,
        itinerary: transformedItinerary,
        cargoParams,
      });
    }

    default:
      throw new Error('No valid data source provided for emissions calculator');
  }
};

// Utility function to check if there is missing vehicle info
export const checkIfMissingKeyVehicleInfo = (formData: EmissionsFormInputsInterface): boolean => {
  return formData.segments.some((segment) => {
    const { vehicle } = segment;

    // For air routes, check if flightNumber is missing
    if (vehicle.type === VEHICLE_TYPES.AIRCRAFT && !vehicle.flightNumber) {
      return true;
    }

    // For ocean routes, check if vehicleId is missing
    if (vehicle.type === VEHICLE_TYPES.VESSEL && !vehicle.vehicleId) {
      return true;
    }

    return false;
  });
};
